import axios from 'axios';

import { AuthStorage } from '../../auth/AuthStorage';
import { JwtUtils } from '../../auth/JwtUtils';

/**
 * AuthApi — handles all authentication flows:
 *
 *  1. Keycloak / Bifrost OIDC  (preferred)
 *     getLoginUrl → redirectToLogin → callback handled by AuthCallback component
 *     Token refresh: POST /api/auth/refresh/
 *
 *  2. Local login  (backup — requires MIMIR_LOCAL_LOGIN_ENABLED=True)
 *     localLogin → short-lived access JWT + 24h rolling refresh JWT
 *     Token refresh: POST /api/auth/local-refresh/
 *
 *  3. Offline grace  (automatic fallback when Keycloak is unreachable)
 *     Keycloak refresh fails (5xx/network) → GET /api/auth/me/ → POST /api/auth/refresh-offline/
 *     Produces an HS256 grace JWT (lifetime set by MIMIR_OFFLINE_GRACE_TTL_SEC, default 5 min,
 *     capped by Keycloak refresh token expiry).
 */
class AuthApi {
    // -------------------------------------------------------------------------
    // Keycloak / Bifrost OIDC
    // -------------------------------------------------------------------------

    /** Fetch the Bifrost login URL, preserving `nextPath` (defaults to current location). */
    getLoginUrl(nextPath) {
        const next =
            nextPath ||
            `${window.location.pathname}${window.location.search.length < 4000 ? window.location.search : ''}`;
        return axios.get('/api/auth/login-url/', { params: { next } }).then((r) => r.data.login_url);
    }

    /** Redirect the browser to Bifrost for Keycloak login, returning to the current URL after. */
    redirectToLogin() {
        return this.getLoginUrl().then((url) => {
            window.location.href = url;
            return new Promise(() => {});
        });
    }

    /** Redirect to Bifrost, returning to an explicit `next` path after login. */
    redirectToLoginUrl(next) {
        const path = decodeURIComponent(next || '/') || '/';
        return this.getLoginUrl(path.startsWith('/') ? path : `/${path}`).then((url) => {
            window.location.href = url;
            return new Promise(() => {});
        });
    }

    // -------------------------------------------------------------------------
    // Auth lifecycle
    // -------------------------------------------------------------------------

    /**
     * Validate stored credentials and exchange them for a fresh token set.
     * Called once on app boot to restore the session.
     *
     * Routing:
     *   local refresh token  → POST /api/auth/local-refresh/
     *   Keycloak token       → POST /api/auth/refresh/ with offline fallback chain
     *   access token only    → GET  /api/auth/me/ (DRF token or no-refresh edge case)
     */
    validate() {
        let user;
        try {
            user = AuthStorage.getUser();
        } catch (err) {
            return Promise.reject(err);
        }
        if (user.refresh_token) {
            return JwtUtils.isLocalRefreshToken(user.refresh_token)
                ? this._localRefresh(user)
                : this._keycloakRefreshWithFallback(user);
        }
        if (user.token) {
            return this._validateWithAccessToken(user);
        }
        return Promise.reject(this._notAuthenticatedError());
    }

    /**
     * Refresh credentials from the currently stored token set.
     * Used by the axios response interceptor when a 401 is received mid-session.
     * Falls back to validate() when no refresh token is present.
     */
    refresh() {
        let user;
        try {
            user = AuthStorage.getUser();
        } catch (err) {
            return Promise.reject(err);
        }
        if (!user.refresh_token) return this.validate();
        return JwtUtils.isLocalRefreshToken(user.refresh_token)
            ? this._localRefresh(user)
            : this._keycloakRefreshWithFallback(user);
    }

    /**
     * Revoke the session on the backend.
     * Reads the refresh token from storage when none is supplied.
     */
    logout(refreshToken) {
        let rt = refreshToken ?? null;
        if (rt == null) {
            try {
                rt = AuthStorage.getUser()?.refresh_token ?? null;
            } catch {
                rt = null;
            }
        }
        if (rt) {
            return axios.post('/api/auth/logout/', { refresh_token: rt });
        }
        return Promise.resolve();
    }

    // -------------------------------------------------------------------------
    // Local login
    // -------------------------------------------------------------------------

    /**
     * Authenticate with a Django username and password (no Keycloak).
     * Requires MIMIR_LOCAL_LOGIN_ENABLED=True on the backend.
     * Returns a short-lived access JWT + 24h rolling refresh JWT.
     */
    localLogin(username, password) {
        return axios
            .post('/api/auth/local-login/', { username, password })
            .then((r) => ({ data: this.buildUserFromResponse(r.data) }));
    }

    // -------------------------------------------------------------------------
    // Private — response builders
    // -------------------------------------------------------------------------

    _notAuthenticatedError() {
        const err = new Error('Not authenticated');
        err.status = 401;
        return err;
    }

    /**
     * Map a token endpoint response (POST /refresh/, /callback/, /local-login/, etc.)
     * to the user object stored in AuthStorage and held in AuthContext state.
     */
    buildUserFromResponse(d) {
        const expiresIn = parseInt(d.expires_in, 10) || 300;
        const authFallback = d.auth_fallback;
        if (authFallback && typeof console !== 'undefined') {
            const username = d.user?.username || '?';
            if (authFallback === 'keycloak_recovered') {
                console.info('[Mimir auth] Keycloak recovered — session restored via RS256 for user=%s', username);
            } else {
                console.warn(
                    '[Mimir auth] offline fallback active: %s (user=%s, expires_in=%ss)',
                    authFallback,
                    username,
                    expiresIn,
                );
            }
        }
        return {
            token: d.access_token,
            refresh_token: d.refresh_token,
            username: d.user?.username,
            user_id: d.user?.user_id,
            staff: Boolean(d.user?.staff),
            permissions: Array.isArray(d.user?.permissions) ? d.user.permissions : [],
            groups: Array.isArray(d.user?.groups) ? d.user.groups : [],
            expiresAt: Date.now() + expiresIn * 1000,
            ...(authFallback ? { authFallback } : {}),
        };
    }

    /**
     * Map a GET /api/auth/me/ response to a user object when Keycloak refresh failed
     * but the existing access token is still accepted by the API.
     */
    _buildUserFromMeData(user, meData) {
        if (typeof console !== 'undefined') {
            console.warn(
                '[Mimir auth] offline fallback active: session_bearer ' +
                    '(Keycloak unreachable; kept session via GET /api/auth/me/)',
            );
        }
        return {
            token: user.token,
            refresh_token: user.refresh_token,
            username: meData.username,
            user_id: meData.user_id,
            staff: Boolean(meData.staff),
            permissions: Array.isArray(meData.permissions) ? meData.permissions : [],
            groups: Array.isArray(meData.groups) ? meData.groups : [],
            expiresAt: JwtUtils.expiresAt(user.token) ?? user.expiresAt ?? Date.now() + 300000,
            authFallback: 'session_bearer',
        };
    }

    // -------------------------------------------------------------------------
    // Private — refresh flows
    // -------------------------------------------------------------------------

    /**
     * True when the error is a transient server/network failure (not an auth rejection).
     * Public so callers (AuthContext, LoginForm) can decide whether to keep the session
     * and retry on reconnect rather than tearing it down and showing an error.
     */
    isTransientFailure(err) {
        if (!err?.response) return true; // network error
        const s = err.response.status;
        return s === 502 || s === 503 || s === 504;
    }

    /** POST /api/auth/local-refresh/ with a mimir_local_refresh JWT. */
    _localRefresh(user) {
        return axios
            .post('/api/auth/local-refresh/', { refresh_token: user.refresh_token })
            .then((r) => ({ data: this.buildUserFromResponse(r.data) }));
    }

    /**
     * GET /api/auth/me/ with the stored Bearer token.
     * Used when the user has only an access token (DRF token or edge case).
     */
    _validateWithAccessToken(user) {
        return axios
            .get('/api/auth/me/', {
                headers: { Authorization: `Bearer ${user.token}` },
            })
            .then((r) => ({
                data: {
                    token: user.token,
                    refresh_token: user.refresh_token,
                    username: r.data.username,
                    user_id: r.data.user_id,
                    staff: Boolean(r.data.staff),
                    permissions: Array.isArray(r.data.permissions) ? r.data.permissions : [],
                    groups: Array.isArray(r.data.groups) ? r.data.groups : [],
                    expiresAt: JwtUtils.expiresAt(user.token) ?? user.expiresAt ?? Date.now() + 300000,
                },
            }));
    }

    /**
     * Keycloak token refresh with a three-layer offline fallback:
     *
     *  Layer 1 — POST /api/auth/refresh/  (normal path)
     *  Layer 2 — GET  /api/auth/me/       (Keycloak unreachable, access token still valid)
     *  Layer 3 — POST /api/auth/refresh-offline/  (grace JWT — token expired or me/ rejected)
     *
     * Layer 2 → 3 escalation happens when GET /api/auth/me/ fails (e.g. network error or 5xx).
     */
    _keycloakRefreshWithFallback(user) {
        return axios
            .post('/api/auth/refresh/', { refresh_token: user.refresh_token })
            .then((r) => ({ data: this.buildUserFromResponse(r.data) }))
            .catch((err) => {
                if (!this.isTransientFailure(err)) throw err;

                if (typeof console !== 'undefined') {
                    console.warn(
                        '[Mimir auth] Keycloak unreachable (HTTP %s); attempting offline fallback...',
                        err?.response?.status ?? 'network error',
                    );
                }

                if (user.token && !JwtUtils.isExpired(user.token)) {
                    return axios
                        .get('/api/auth/me/', {
                            headers: { Authorization: `Bearer ${user.token}` },
                        })
                        .then((r) => ({ data: this._buildUserFromMeData(user, r.data) }))
                        .catch((meErr) => {
                            if (this.isTransientFailure(meErr)) {
                                return this._offlineGraceFallback(user, err);
                            }
                            throw meErr;
                        });
                }

                return this._offlineGraceFallback(user, err);
            });
    }

    /** POST /api/auth/refresh-offline/ — last resort when Keycloak is down and me/ also fails. */
    _offlineGraceFallback(user, originalErr) {
        if (!user.refresh_token || !user.token) throw originalErr;
        return axios
            .post('/api/auth/refresh-offline/', {
                refresh_token: user.refresh_token,
                access_token: user.token,
            })
            .then((r) => ({ data: this.buildUserFromResponse(r.data) }))
            .catch((graceErr) => {
                if (typeof console !== 'undefined') {
                    console.warn(
                        '[Mimir auth] offline grace fallback also failed (HTTP %s): %s',
                        graceErr?.response?.status ?? 'network error',
                        graceErr?.response?.data?.detail ?? graceErr.message,
                    );
                }
                throw originalErr;
            });
    }
}

export const auth = new AuthApi();

// Kept for backwards compatibility — prefer AuthStorage directly in new code.
export { AuthStorage };
export const getAuthStorage = () => AuthStorage.getStorage();
